import { useLazyQuery, useQuery } from '@apollo/client/react';
import CheckIcon from '@mui/icons-material/Check';
import ClearIcon from '@mui/icons-material/Clear';
import EditIcon from '@mui/icons-material/Edit';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import SearchIcon from '@mui/icons-material/Search';
import VisibilityIcon from '@mui/icons-material/Visibility';
import {
  Box, FormControl, Grid, InputAdornment,
  InputLabel, MenuItem, Select, TextField, Tooltip,
} from '@mui/material';
import { useEffect, useMemo, useState, useRef } from 'react';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import { adaptDelegacionArray } from '../../../adapters/delegacionAdapter';
import { adaptTipoProfesionalArray } from '../../../adapters/tipoProfesionalAdapter';
import { ButtonOptionGroup } from '../../../components/buttonOptionGroup/ButtonOptionGroup';
import { CustomButton } from '../../../components/customButton/CustomButton';
import DataGridGenerico from '../../../components/dataGrid/DataGridGenerico';
import { generateColumnsDefaultConfiguration } from '../../../components/dataGrid/columnHelpers';
import { PopoverModal } from '../../../components/popoverModal/PopoverModal';
import { usePersistFilters } from '../../../hooks/usePersistFilters';
import { GET_DELEGACIONES_BY_USUARIO } from '../../../data/graphQL/queries/PacienteProbable/getAllDelegacionesByUsuario';
import { GET_ALL_TIPO_PROFESIONALES } from '../../../data/graphQL/queries/PlanCuidado/getTipoProfesionales';
import { GET_USUARIO_DELEGACION_ORIGEN } from '../../../data/graphQL/queries/usuarios/getUsuarioDelegacionOrigen';
import {
  GET_HONORARIOS_FIDELIZADOS,
  adaptHonorariosFidelizadosArray,
} from '../../../data/graphQL/queries/HonorarioFidelizado/getHonorariosFidelizados';
import { downloadExcelGeneric } from '../../../utils/downloadExcelGeneric';

const idUsuarioLoggeado = sessionStorage.getItem('idUsuario');

const ESTADO_ACTIVOS = 'Activos';
const ESTADO_INACTIVOS = 'Inactivos';
const estadosOptions = [ESTADO_ACTIVOS, ESTADO_INACTIVOS];

const ID_TODAS_DELEGACIONES = -1;
const ID_TODOS_TIPO_PROFESIONAL = -1;

const ITEM_TODAS = { id: ID_TODAS_DELEGACIONES, displayName: 'TODAS' };
const ITEM_TODOS = { id: ID_TODOS_TIPO_PROFESIONAL, displayName: 'TODOS' };

export const HonorariosFidelizadosContent = ({ allowedWrite, setChildrenAction }) => {
  const navigate = useNavigate();

  const [delegaciones, setDelegaciones] = useState([]);
  const [tipoProfesionales, setTipoProfesionales] = useState([]);
  const [honorarios, setHonorarios] = useState([]);
  const [datosOriginal, setDatosOriginal] = useState([]);
  const [loading, setLoading] = useState(false);

  const { filters, handleChangeFilters } = usePersistFilters({
    idDelegacion: ID_TODAS_DELEGACIONES,
    idTipoProfesional: ID_TODOS_TIPO_PROFESIONAL,
    txtFiltro: '',
    estado: ESTADO_ACTIVOS,
  }, 'HonorariosFidelizadosFilter');

  const delegacionesRef = useRef([]);
  const tipoProfesionalesRef = useRef([]);

  // ── Combos ────────────────────────────────────────────────────────────────

  const [getDelegaciones, { data: dataDelegaciones }] = useLazyQuery(
    GET_DELEGACIONES_BY_USUARIO,
    { fetchPolicy: 'no-cache' }
  );

  useEffect(() => {
    if (!dataDelegaciones) return;
    let adaptadas = adaptDelegacionArray(dataDelegaciones);
    adaptadas = adaptadas?.filter(x => x.id !== null);
    setDelegaciones([ITEM_TODAS, ...adaptadas]);
  }, [dataDelegaciones]);

  const { data: dataTipos } = useQuery(GET_ALL_TIPO_PROFESIONALES);

  useEffect(() => {
    if (!dataTipos) return;
    setTipoProfesionales([ITEM_TODOS, ...adaptTipoProfesionalArray(dataTipos)]);
  }, [dataTipos]);

  const [getDelegacionOrigen] = useLazyQuery(
    GET_USUARIO_DELEGACION_ORIGEN,
    { fetchPolicy: 'no-cache' }
  );

  useEffect(() => {
    delegacionesRef.current = delegaciones;
  }, [delegaciones]);

  useEffect(() => {
    tipoProfesionalesRef.current = tipoProfesionales;
  }, [tipoProfesionales]);

  useEffect(() => {
    buscar();
  }, []);

  useEffect(() => {
    const idUsuario = JSON.parse(idUsuarioLoggeado);
    getDelegaciones({ variables: { idUsuario } });
    getDelegacionOrigen({ variables: { idUsuario } });
  }, []);

  // ── Query principal ───────────────────────────────────────────────────────

  const [getHonorarios, { data: dataHonorarios, error: errorHonorarios }] = useLazyQuery(
    GET_HONORARIOS_FIDELIZADOS,
    { fetchPolicy: 'no-cache' }
  );

  useEffect(() => {
    if (!dataHonorarios) return;
    const adapted = adaptHonorariosFidelizadosArray(dataHonorarios);
    setDatosOriginal(adapted);
    setHonorarios(filtrarPorTexto(filters.txtFiltro, adapted));
    setLoading(false);
  }, [dataHonorarios]);

  useEffect(() => {
    if (!errorHonorarios) return;
    toast.error('No se pudo cargar la tabla de honorarios fidelizados.');
    setLoading(false);
  }, [errorHonorarios]);

  const buscar = () => {
    setLoading(true);
    getHonorarios({
      variables: {
        idDelegacion: filters.idDelegacion === ID_TODAS_DELEGACIONES ? null : filters.idDelegacion,
        idTipoProfesional: filters.idTipoProfesional === ID_TODOS_TIPO_PROFESIONAL ? null : filters.idTipoProfesional,
        activo: filters.estado === ESTADO_ACTIVOS,
      },
    });
  };

  // ── Filtro cliente ────────────────────────────────────────────────────────

  const filtrarPorTexto = (texto, datos) => {
    if (!texto) return datos;
    const lower = texto.toLowerCase();
    return datos.filter((h) =>
      [h.descripcion, h.figura, h.clase, String(h.importe), h.fechaExpira]
        .some((v) => v?.toLowerCase().includes(lower))
    );
  };

  const handleTextFilter = () => setHonorarios(filtrarPorTexto(filters.txtFiltro, datosOriginal));
  const handleTextClean = () => { handleChangeFilters('txtFiltro', ''); setHonorarios(datosOriginal); };

  // ── Navegación ────────────────────────────────────────────────────────────

  const handleNuevo = () =>
    navigate('/honorarios/fidelizados/nuevo', {
      state: {
        idHonorarioFidelizado: 0,
        isNew: true,
        allowedWrite: true,
        delegaciones: delegacionesRef.current,
        tipoProfesionales: tipoProfesionalesRef.current,
      },
    });

  const handleEditar = (row) =>
    navigate('/honorarios/fidelizados/nuevo', {
      state: {
        idHonorarioFidelizado: row.id,
        isNew: false,
        allowedWrite: true,
        delegaciones: delegacionesRef.current,
        tipoProfesionales: tipoProfesionalesRef.current,
      },
    });

  const handleVer = (row) =>
    navigate('/honorarios/fidelizados/nuevo', {
      state: {
        idHonorarioFidelizado: row.id,
        isNew: false,
        allowedWrite: false,
        delegaciones: delegacionesRef.current,
        tipoProfesionales: tipoProfesionalesRef.current,
      },
    });

  // ── Exportar Excel ────────────────────────────────────────────────────────

  const handleExportar = () => {
    const delegNombre = delegaciones.find((d) => d.id === filters.idDelegacion)?.displayName ?? 'TODAS';
    const tipoNombre = tipoProfesionales.find((t) => t.id === filters.idTipoProfesional)?.displayName ?? 'TODOS';

    downloadExcelGeneric({
      fileName: `HonorariosFidelizados_${delegNombre}_${tipoNombre}_${filters.estado}`,
      sheet: 'Honorarios Fidelizados',
      tablePayload: {
        header: ['ID', 'Delegación', 'Descripción', 'Tipo Profesional', 'Figura', 'Clase', 'Cant. Pacientes', 'Importe', 'Fecha Expira'],
        body: honorarios.map((h) => ({
          'ID': h.id,
          'Delegación': h.delegacion,
          'Descripción': h.descripcion,
          'Tipo Profesional': h.tipoProfesional,
          'Figura': h.figura,
          'Clase': h.clase,
          'Importe': !!h.importe ? '$' + h.importe : '',
          'Fecha Expira': h.fechaExpira?.split('T')[0] ?? '',
        })),
      },
    });
  };

  // ── Columnas ──────────────────────────────────────────────────────────────
  const columns = useMemo(() => generateColumnsDefaultConfiguration([
    {
      field: 'action', headerName: '', width: 40, sortable: false,
      renderCell: ({ row }) => (
        <PopoverModal>
          <div className='popover-modal-content'>
            <div className='item-option clickable-option' onClick={() => handleVer(row)} aria-hidden='true'>
              <VisibilityIcon className='edit-icon' />
              <span className='toggle-action'>Ver</span>
            </div>
            {allowedWrite && (
              <div className='item-option clickable-option' onClick={() => handleEditar(row)} aria-hidden='true'>
                <EditIcon className='edit-icon' />
                <span className='toggle-action'>Editar</span>
              </div>
            )}
          </div>
        </PopoverModal>
      ),
    },
    { field: 'id', headerName: 'ID', width: 70 },
    { field: 'delegacion', headerName: 'Delegación', width: 160 },
    { field: 'descripcion', headerName: 'Descripción', width: 180 },
    { field: 'tipoProfesional', headerName: 'Tipo Profesional', width: 150 },
    { field: 'figura', headerName: 'Figura', width: 130 },
    { field: 'clase', headerName: 'Clase', width: 100 },
    {
      field: 'importe', headerName: 'Importe', width: 120,
      valueGetter: (v) => '$' + parseFloat(v || 0).toLocaleString('es-AR'),
    },
    {
      field: 'fechaExpira', headerName: 'Fecha Expira', width: 130, type: 'date',
      valueGetter: (v) => v?.split('T')[0] ?? '',
    }
  ]), [allowedWrite]);

  // ── Botón header ──────────────────────────────────────────────────────────

  useEffect(() => {
    setChildrenAction(() => (
      <Box sx={{ display: 'flex', gap: 1 }}>
        <CustomButton
          label='Exportar'
          variant='outlined'
          startIcon={<FileDownloadIcon />}
          disabled={!honorarios.length}
          onClick={handleExportar}
        />
        <CustomButton
          label='Nuevo'
          variant='contained'
          disabled={!allowedWrite || !delegaciones.length || !tipoProfesionales.length}
          onClick={handleNuevo}
        />
      </Box>
    ));
  }, [setChildrenAction, honorarios, delegaciones, tipoProfesionales, allowedWrite]);

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <div className='grid-container'>
      <Grid container spacing={2} sx={{ mb: 3 }}>

        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <FormControl fullWidth size='small' sx={{ backgroundColor: 'white' }}>
            <InputLabel>Delegación</InputLabel>
            <Select
              label='Delegación'
              value={filters.idDelegacion}
              onChange={(e) => handleChangeFilters('idDelegacion', e.target.value)}
            >
              {delegaciones.map((d) => (
                <MenuItem key={d.id} value={d.id}>{d.displayName}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <FormControl fullWidth size='small' sx={{ backgroundColor: 'white' }}>
            <InputLabel>Tipo Profesional</InputLabel>
            <Select
              label='Tipo Profesional'
              value={filters.idTipoProfesional}
              onChange={(e) => handleChangeFilters('idTipoProfesional', e.target.value)}
            >
              {tipoProfesionales.map((t) => (
                <MenuItem key={t.id} value={t.id}>{t.displayName}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <TextField
            label='Filtrar'
            size='small'
            fullWidth
            sx={{ backgroundColor: 'white' }}
            value={filters.txtFiltro}
            onChange={(e) => handleChangeFilters('txtFiltro', e.target.value)}
            onKeyUp={handleTextFilter}
            InputProps={{
              endAdornment: (
                <InputAdornment position='end'>
                  <SearchIcon sx={{ cursor: 'pointer' }} onClick={handleTextFilter} />
                  <ClearIcon sx={{ cursor: 'pointer' }} onClick={handleTextClean} />
                </InputAdornment>
              ),
            }}
          />
        </Grid>

        <Grid size={{ xs: 12, sm: 6, md: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
            <ButtonOptionGroup
              key={filters.estado}
              options={estadosOptions}
              onOptionClick={(index) => handleChangeFilters('estado', estadosOptions[index])}
              selectedOption={estadosOptions.indexOf(filters.estado)}
            />
          </Box>
        </Grid>

        <Grid size={{ xs: 12, sm: 6, md: 1 }}>
          <CustomButton
            label='Buscar'
            variant='contained'
            fullWidth
            onClick={buscar}
          />
        </Grid>

      </Grid>

      <DataGridGenerico
        columns={columns}
        rows={honorarios}
        totalCount={honorarios.length}
        loading={loading}
        toolbar={false}
      />
    </div>
  );
};